import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import Lightbox from './Lightbox';

interface PhotoGalleryProps {
  images: string[];
  alt?: string;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ images, alt = 'Property image' }) => {
  const [current, setCurrent] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!images || images.length === 0) {
    return (
      <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
        <p className="text-muted-foreground">Keine Bilder vorhanden</p>
      </div>
    );
  }

  const prev = () => setCurrent(i => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setCurrent(i => (i === images.length - 1 ? 0 : i + 1));

  // Keyboard navigation (disabled while lightbox handles keys)
  useEffect(() => {
    if (lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen]);

  return (
    <>
      <div className="relative w-full overflow-hidden rounded-xl group" style={{ aspectRatio: '16/7' }}>
        {/* Image */}
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="block w-full h-full cursor-zoom-in"
          aria-label="Bild vergrößern"
        >
          <img
            key={current}
            src={images[current]}
            alt={`${alt} ${current + 1}`}
            className="w-full h-full object-cover"
          />
        </button>

        {/* Expand hint */}
        <div className="absolute top-3 right-3 bg-black/50 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <Expand className="h-4 w-4" />
        </div>

        {/* Left arrow */}
        {images.length > 1 && (
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/65 text-white rounded-full p-2 transition-colors"
            aria-label="Vorheriges Bild"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
        )}

        {/* Right arrow */}
        {images.length > 1 && (
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/65 text-white rounded-full p-2 transition-colors"
            aria-label="Nächstes Bild"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        )}

        {/* Counter */}
        {images.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs font-medium px-2.5 py-1 rounded-full">
            {current + 1} / {images.length}
          </div>
        )}

        {/* Dot indicators */}
        {images.length > 1 && images.length <= 10 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === current ? 'bg-white scale-125' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && (
        <Lightbox
          images={images}
          startIndex={current}
          alt={alt}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

export default PhotoGallery;
